import api from '@api';
import { withArchiveMode } from 'src/utils/archiveMode';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import {
    getOrderDetailFetchSuccess,
    getOrderDetailFetchFailure,
    getOrderDetailFilesFetch as getOrderDetailFilesFetchAction,
    getOrderDetailFilesFetchSuccess,
    getOrderDetailFilesFetchFailure,
    getOrderDetailProductsFetchSuccess,
    getOrderDetailProductsFetchFailure,
    getOrderDetailProductsFilesFetch as getOrderDetailProductsFilesFetchAction,
    getOrderDetailProductsFilesFetchSuccess,
    getOrderDetailProductsFilesFetchFailure,
    getOrderDetailChangelogFetchSuccess,
    getOrderDetailChangelogFetchFailure,
    getOrderDetailChangelogFetch as getOrderDetailChangelogFetchAction,
    getOrderDetailExpeditionPackagesFetchSuccess,
    getOrderDetailExpeditionPackagesFetchFailure,
    getOrderDetailTransportHistoryFetchSuccess,
    getOrderDetailTransportHistoryFetchFailure,
    getOrderDetailTransportsFetchSuccess,
    getOrderDetailTransportsFetchFailure,
    getOrderDetailClaimsReturnsFetchSuccess,
    getOrderDetailClaimsReturnsFetchFailure,
    createOrderDetailFetchSuccess,
    createOrderDetailFetchFailure,
    updateOrderDetailFetchSuccess,
    updateOrderDetailFetchFailure,
    patchOrderDetailFetchSuccess,
    patchOrderDetailFetchFailure,
    editOrderDetailEshopNoteFetchSuccess,
    editOrderDetailEshopNoteFetchFailure,
    createInvoiceOrderDetailFetchSuccess,
    createInvoiceOrderDetailFetchFailure,
    getOrderPalletsFetchSuccess,
    getOrderPalletsFetchFailure,
    getPalletTypesFetchSuccess, getPalletTypesFetchFailure
} from './reducer';
import { OrderType } from 'src/types/order';
import translate from '@translate';
import {
    OrderDetailFilesResType,
    OrderDetailPalletsResType,
    OrderDetailPalletTypeResType,
    OrderDetailResType
} from 'src/types/orderDetail';
import { CLAIMS_COLUMNS, RETURNS_COLUMNS } from './constants';
import { ClaimsGridResType } from 'src/types/claimsGrid';
import { ReturnsGridResType } from 'src/types/returnsGrid';
import { RootStoreType } from 'src/types/store';

const getOrderDetailFetchRequest = async (idOrder: string) => {
    const res = await api(
        'get',
        withArchiveMode(`api/secure/orders/${idOrder}?exclude=products,productFiles,productSets`)
    );
    return res.data;
};

function* getOrderDetailFetch(action: { type: string; payload: string }) {
    try {
        const response: OrderDetailResType = yield call((): unknown => getOrderDetailFetchRequest(action.payload));
        yield put(getOrderDetailFetchSuccess(response));
    } catch (error) {
        yield put(getOrderDetailFetchFailure({ error }));
    }
}

export function* getOrderDetailFetchWatch() {
    yield takeEvery('orderDetail/getOrderDetailFetch', getOrderDetailFetch);
}

const getOrderDetailProductsFetchRequest = async (idOrder: string) => {
    const res = await api('get', withArchiveMode(`api/secure/orders/${idOrder}/products`));
    return res.data;
};

function* getOrderDetailProductsFetch(action: { type: string; payload: string }) {
    try {
        const response: {
            data: {
                ORDER_PRODUCTS: OrderDetailResType['data']['ORDER_PRODUCTS'];
                ORDER_PRODUCT_SETS: OrderDetailResType['data']['ORDER_PRODUCT_SETS'];
            };
        } = yield call((): unknown => getOrderDetailProductsFetchRequest(action.payload));
        yield put(getOrderDetailProductsFetchSuccess(response.data));
    } catch (error) {
        yield put(getOrderDetailProductsFetchFailure({ error }));
    }
}

export function* getOrderDetailProductsFetchWatch() {
    yield takeEvery('orderDetail/getOrderDetailProductsFetch', getOrderDetailProductsFetch);
}

const getOrderDetailFilesFetchRequest = async (idOrder: string) => {
    const res = await api('get', withArchiveMode(`api/secure/orders/files/${idOrder}`));
    return res.data;
};

function* getOrderDetailFilesFetch(action: { type: string; payload: string }) {
    try {
        const response: OrderDetailFilesResType = yield call((): Promise<OrderDetailFilesResType> =>
            getOrderDetailFilesFetchRequest(action.payload)
        );
        yield put(
            getOrderDetailFilesFetchSuccess({
                orderId: Number(action.payload),
                FILES: response.FILES,
            })
        );
    } catch (error) {
        yield put(getOrderDetailFilesFetchFailure({ error }));
    }
}

export function* getOrderDetailFilesFetchWatch() {
    yield takeEvery('orderDetail/getOrderDetailFilesFetch', getOrderDetailFilesFetch);
}

const getOrderDetailProductsFilesFetchRequest = async (idOrder: string) => {
    const res = await api('get', `api/secure/orders/${idOrder}/products/files`);
    return res.data.data;
};

function* getOrderDetailProductsFilesFetch(action: { type: string; payload: string }) {
    try {
        const response: OrderDetailFilesResType = yield call((): any =>
            getOrderDetailProductsFilesFetchRequest(action.payload)
        );
        yield put(getOrderDetailProductsFilesFetchSuccess(response));
    } catch (error) {
        yield put(getOrderDetailProductsFilesFetchFailure({ error }));
    }
}

export function* getOrderDetailProductsFilesFetchWatch() {
    yield takeEvery('orderDetail/getOrderDetailProductsFilesFetch', getOrderDetailProductsFilesFetch);
}

const createOrderDetailFetchRequest = async (data: OrderType, mode?:string) => {
    let url = 'api/secure/orders?system=myskladon';
    if (mode) {
        url += `&mode=${mode}`;
    }
    const res = await api('post', url, data);
    return res.data;
};

function* createOrderDetailFetch(action: { type: string; payload: {data: OrderType, mode?: string }}) {
    try {
        const allowOrderProductFiles: string = yield select(
            (state: RootStoreType) => state.provider.data?.ALLOW_ORDER_PRODUCT_DETAIL
        );
        const response: OrderDetailResType = yield call((): any => createOrderDetailFetchRequest(action.payload.data, action.payload.mode));
        yield put(
            createOrderDetailFetchSuccess({
                ...response,
                messageSuccess: { title: translate('orders.message.orderCreated'), text: response.message },
            })
        );
        // Files load after navigate settles via Transport-tab lazy fetch (avoids race with clearOrderDetail).
        if (allowOrderProductFiles) {
            yield put(getOrderDetailProductsFilesFetchAction(response.data.ID));
        }
        yield put(getOrderDetailChangelogFetchAction(response.data.ID));
    } catch (error) {
        yield put(createOrderDetailFetchFailure({ error }));
    }
}

export function* createOrderDetailFetchWatch() {
    yield takeEvery('orderDetail/createOrderDetailFetch', createOrderDetailFetch);
}

const updateOrderDetailFetchRequest = async (params: { idOrder: string; data: OrderType }) => {
    const res = await api('put', `api/secure/orders/${params.idOrder}?system=myskladon`, params.data);
    return res.data;
};

function* updateOrderDetailFetch(action: {
    type: string;
    payload: { idOrder: string; data: OrderType; callback?: (succes: boolean) => void };
}) {
    try {
        const allowOrderProductFiles: string = yield select(
            (state: RootStoreType) => state.provider.data?.ALLOW_ORDER_PRODUCT_DETAIL
        );
        const response: OrderDetailResType = yield call((): any => updateOrderDetailFetchRequest(action.payload));
        yield put(
            updateOrderDetailFetchSuccess({
                ...response,
                messageSuccess: { title: translate('orders.message.orderUpdate'), text: response.message },
            })
        );
        if (action.payload.callback) {
            action.payload.callback(true);
        }
        yield put(getOrderDetailFilesFetchAction(action.payload.idOrder));
        if (allowOrderProductFiles) {
            yield put(getOrderDetailProductsFilesFetchAction(response.data.ID));
        }
        yield put(getOrderDetailChangelogFetchAction(action.payload.idOrder));
    } catch (error) {
        yield put(updateOrderDetailFetchFailure({ error }));
        if (action.payload.callback) {
            action.payload.callback(false);
        }
    }
}

export function* updateOrderDetailFetchWatch() {
    yield takeEvery('orderDetail/updateOrderDetailFetch', updateOrderDetailFetch);
}

const patchOrderDetailFetchRequest = async (params: { idOrder: string; data: OrderType }) => {
    const res = await api('patch', `api/secure/orders/${params.idOrder}?system=myskladon`, params.data);
    return res.data;
};

function* patchOrderDetailFetch(action: { type: string; payload: { idOrder: string; data: OrderType } }) {
    try {
        const allowOrderProductFiles: string = yield select(
            (state: RootStoreType) => state.provider.data?.ALLOW_ORDER_PRODUCT_DETAIL
        );
        const response: OrderDetailResType = yield call((): any => patchOrderDetailFetchRequest(action.payload));
        yield put(
            patchOrderDetailFetchSuccess({
                ...response,
                messageSuccess: { title: translate('orders.message.orderUpdate') },
            })
        );
        yield put(getOrderDetailFilesFetchAction(action.payload.idOrder));
        if (allowOrderProductFiles) {
            yield put(getOrderDetailProductsFilesFetchAction(response.data.ID));
        }
        yield put(getOrderDetailChangelogFetchAction(action.payload.idOrder));
    } catch (error) {
        yield put(patchOrderDetailFetchFailure({ error }));
    }
}

export function* patchOrderDetailFetchWatch() {
    yield takeEvery('orderDetail/patchOrderDetailFetch', patchOrderDetailFetch);
}

const editOrderDetailEshopNoteFetchRequest = async (params: { idOrder: string; data: { ESHOP_NOTE: string } }) => {
    const res = await api('put', `api/secure/orders/eshop-note/${params.idOrder}?system=myskladon`, params.data);
    return res.data;
};

function* editOrderDetailEshopNoteFetch(action: {
    type: string;
    payload: { idOrder: string; data: { ESHOP_NOTE: string } };
}) {
    try {
        const allowOrderProductFiles: string = yield select(
            (state: RootStoreType) => state.provider.data?.ALLOW_ORDER_PRODUCT_DETAIL
        );
        const response: OrderDetailResType = yield call((): any =>
            editOrderDetailEshopNoteFetchRequest(action.payload)
        );
        yield put(
            editOrderDetailEshopNoteFetchSuccess({
                ...response,
                messageSuccess: { title: translate('orders.message.orderUpdate') },
            })
        );
        yield put(getOrderDetailFilesFetchAction(action.payload.idOrder));
        if (allowOrderProductFiles) {
            yield put(getOrderDetailProductsFilesFetchAction(response.data.ID));
        }
        yield put(getOrderDetailChangelogFetchAction(action.payload.idOrder));
    } catch (error) {
        yield put(editOrderDetailEshopNoteFetchFailure({ error }));
    }
}

export function* editOrderDetailEshopNoteFetchWatch() {
    yield takeEvery('orderDetail/editOrderDetailEshopNoteFetch', editOrderDetailEshopNoteFetch);
}

const getOrderDetailChangelogFetchRequest = async (idOrder: string) => {
    const res = await api('get', withArchiveMode(`api/secure/orders/changelog/${idOrder}`));
    return res.data;
};

function* getOrderDetailChangelogFetch(action: { type: string; payload: string }) {
    try {
        const response: OrderDetailResType = yield call((): any => getOrderDetailChangelogFetchRequest(action.payload));
        yield put(getOrderDetailChangelogFetchSuccess(response));
    } catch (error) {
        yield put(getOrderDetailChangelogFetchFailure({ error }));
    }
}

export function* getOrderDetailChangelogFetchWatch() {
    yield takeEvery('orderDetail/getOrderDetailChangelogFetch', getOrderDetailChangelogFetch);
}

const getOrderDetailExpeditionPackagesFetchRequest = async (idOrder: string) => {
    const res = await api('get', `api/secure/order-expedition-packages/${idOrder}/products-with-details`);
    return res.data;
};

function* getOrderDetailExpeditionPackagesFetch(action: { type: string; payload: string }) {
    try {
        const response: OrderDetailResType = yield call((): any =>
            getOrderDetailExpeditionPackagesFetchRequest(action.payload)
        );
        yield put(getOrderDetailExpeditionPackagesFetchSuccess(response));
    } catch (error) {
        yield put(getOrderDetailExpeditionPackagesFetchFailure({ error }));
    }
}

export function* getOrderDetailExpeditionPackagesFetchWatch() {
    yield takeEvery('orderDetail/getOrderDetailExpeditionPackagesFetch', getOrderDetailExpeditionPackagesFetch);
}

const getOrderDetailTransportHistoryFetchRequest = async (idOrder: string) => {
    const res = await api('get', withArchiveMode(`api/secure/orders/shipping-tracking/${idOrder}`));
    return res.data.data;
};

function* getOrderDetailTransportHistoryFetch(action: { type: string; payload: string }) {
    try {
        const response: OrderDetailResType = yield call((): any =>
            getOrderDetailTransportHistoryFetchRequest(action.payload)
        );
        yield put(getOrderDetailTransportHistoryFetchSuccess(response));
    } catch (error) {
        yield put(getOrderDetailTransportHistoryFetchFailure({ error }));
    }
}

export function* getOrderDetailTransportHistoryFetchWatch() {
    yield takeEvery('orderDetail/getOrderDetailTransportHistoryFetch', getOrderDetailTransportHistoryFetch);
}

const getOrderDetailTransportsFetchRequest = async (idOrder: string) => {
    const res = await api('get', withArchiveMode(`api/secure/orders/${idOrder}/transports`));
    return res.data;
};

function* getOrderDetailTransportsFetch(action: { type: string; payload: string }) {
    try {
        const response: { data: unknown } = yield call((): Promise<{ data: unknown }> =>
            getOrderDetailTransportsFetchRequest(action.payload)
        );
        yield put(getOrderDetailTransportsFetchSuccess(response));
    } catch (error) {
        yield put(getOrderDetailTransportsFetchFailure({ error }));
    }
}

export function* getOrderDetailTransportsFetchWatch() {
    yield takeEvery('orderDetail/getOrderDetailTransportsFetch', getOrderDetailTransportsFetch);
}

const getOrderDetailClaimsFetchRequest = async (idOrder: string) => {
    const res = await api('post', withArchiveMode(`api/secure/claims/grid`), {
        filters: [[{ column: 'ORDER_ID', operator: 'eq', value: idOrder }]],
        sorts: [{ column: 'CREATED_AT', direction: 'DESC' }],
        page: 1,
        items: 100,
        columns: CLAIMS_COLUMNS,
    });
    return res.data.data;
};
const getOrderDetailReturnsFetchRequest = async (idOrder: string) => {
    const res = await api('post', withArchiveMode(`api/secure/returns/grid`), {
        filters: [[{ column: 'ORDER_ID', operator: 'eq', value: idOrder }]],
        sorts: [{ column: 'CREATED_AT', direction: 'DESC' }],
        page: 1,
        items: 100,
        columns: RETURNS_COLUMNS,
    });
    return res.data.data;
};

function* getOrderDetailClaimsReturnsFetch(action: { type: string; payload: string }) {
    try {
        const responseClaims: ClaimsGridResType = yield call((): any =>
            getOrderDetailClaimsFetchRequest(action.payload)
        );
        const responseReturns: ReturnsGridResType = yield call((): any =>
            getOrderDetailReturnsFetchRequest(action.payload)
        );
        yield put(
            getOrderDetailClaimsReturnsFetchSuccess({ claims: responseClaims.data, returns: responseReturns.data })
        );
    } catch (error) {
        yield put(getOrderDetailClaimsReturnsFetchFailure({ error }));
    }
}

export function* getOrderDetailClaimsReturnsFetchWatch() {
    yield takeEvery('orderDetail/getOrderDetailClaimsReturnsFetch', getOrderDetailClaimsReturnsFetch);
}

const createInvoiceOrderDetailFetchRequest = async (idOrder: string) => {
    const res = await api('get', `api/secure/orders/create-invoice/${idOrder}`);
    return res.data;
};

function* createInvoiceOrderDetailFetch(action: { type: string; payload: string }) {
    try {
        const response: OrderDetailResType = yield call((): any =>
            createInvoiceOrderDetailFetchRequest(action.payload)
        );
        yield put(
            createInvoiceOrderDetailFetchSuccess({
                ...response,
                messageSuccess: { title: translate('orders.message.orderIvoiceCreated') },
            })
        );
        yield put(getOrderDetailFilesFetchAction(action.payload));
        yield put(getOrderDetailProductsFilesFetchAction(action.payload));
        yield put(getOrderDetailChangelogFetchAction(action.payload));
    } catch (error) {
        yield put(createInvoiceOrderDetailFetchFailure({ error }));
    }
}

export function* createInvoiceOrderDetailFetchWatch() {
    yield takeEvery('orderDetail/createInvoiceOrderDetailFetch', createInvoiceOrderDetailFetch);
}

const getOrderPalletsFetchRequest = async (idOrder: string) => {
    const res = await api('get', `api/secure/order-pallets/${idOrder}`);
    return res.data;
}

function* getOrderPalletsFetch(action: { type: string; payload: string }) {
    try {
        const response: OrderDetailPalletsResType = yield call((): any => getOrderPalletsFetchRequest(action.payload));
        yield put(getOrderPalletsFetchSuccess(response));
    } catch (error) {
        yield put(getOrderPalletsFetchFailure({ error }));
    }
}

export function* getOrderPalletsFetchWatch() {
    yield takeEvery('orderDetail/getOrderPalletsFetch', getOrderPalletsFetch);
}

const getPalletTypesFetchRequest = async () => {
    const res = await api('get', `api/secure/order-pallets/pallet-types`);
    return res.data;
}

function* getPalletTypesFetch() {
    try {
        const response: OrderDetailPalletTypeResType = yield call(getPalletTypesFetchRequest);
        yield put(getPalletTypesFetchSuccess(response));
    } catch (error) {
        yield put(getPalletTypesFetchFailure({ error }));
    }
}

export function* getPalletTypesFetchWatch() {
    yield takeEvery('orderDetail/getPalletTypesFetch', getPalletTypesFetch);
}
